import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { ProductView, Product } from "../ProductView/ProductView";
import { Product as ApiProduct } from "../../api/productService";
import { useCartStore } from "../../store/useStore";
import { useProducts } from "../../hooks/useProducts";

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (max-width: 768px) {
    padding: 0 0.5rem;
  }

  @media (max-width: 480px) {
    padding: 0 0.25rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 300px));
  gap: 2rem;
  justify-content: center;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(180px, 250px));
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
`;

const Card = styled.div`
  background: #f4efef;
  border-radius: 1rem;
  box-shadow: 0 2px 12px rgba(60, 70, 54, 0.07);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  transition: box-shadow 0.2s, transform 0.2s;
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  user-select: none;

  &:hover {
    box-shadow: 0 4px 24px rgba(60, 70, 54, 0.13);
    transform: translateY(-4px) scale(1.03);
  }

  @media (max-width: 768px) {
    max-width: 100%;
    border-radius: 0.8rem;
  }

  @media (max-width: 480px) {
    border-radius: 0.6rem;

    &:hover {
      transform: translateY(-2px) scale(1.02);
    }
  }
`;

const ProductImage = styled.img`
  width: 100%;
  height: 180px;
  object-fit: cover;

  @media (max-width: 768px) {
    height: 160px;
  }

  @media (max-width: 480px) {
    height: 140px;
  }
`;

const ImageCount = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;

  @media (max-width: 768px) {
    top: 6px;
    right: 6px;
    padding: 3px 6px;
    font-size: 0.75rem;
    border-radius: 8px;
  }

  @media (max-width: 480px) {
    top: 4px;
    right: 4px;
    padding: 2px 4px;
    font-size: 0.7rem;
    border-radius: 6px;
  }
`;

const ProductTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  margin: 0.7rem 0 0.3rem 0;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin: 0.6rem 0 0.25rem 0;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin: 0.5rem 0 0.2rem 0;
  }
`;

const ProductPrice = styled.div`
  color: #e97d7dff;
  font-weight: 700;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin-bottom: 0.8rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-bottom: 0.6rem;
  }
`;

const AddToCart = styled.button`
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  background: #e97d7dff;
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 38px;
  height: 38px;
  display: none;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(126, 156, 122, 0.08);
  transition: background 0.2s;
  z-index: 2;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background: #5c6650;
  }

  ${Card}:hover & {
    display: flex;
  }

  @media (max-width: 1024px) {
    display: none !important;
  }
`;

const SkeletonCard = styled.div`
  background: #f4efef;
  border-radius: 1rem;
  box-shadow: 0 2px 12px rgba(60, 70, 54, 0.07);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
    100% {
      opacity: 1;
    }
  }
`;

const SkeletonImage = styled.div`
  width: 100%;
  height: 200px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  margin-bottom: 1rem;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  @media (max-width: 768px) {
    height: 180px;
  }

  @media (max-width: 480px) {
    height: 160px;
  }
`;

const SkeletonText = styled.div`
  width: 80%;
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  margin-bottom: 0.5rem;
  border-radius: 4px;
`;

const SkeletonPrice = styled.div`
  width: 60%;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  margin-bottom: 1rem;
  border-radius: 4px;
`;

const SkeletonButton = styled.div`
  width: 100px;
  height: 32px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 16px;
  margin-bottom: 1rem;
`;

const ComingSoonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 2rem 1rem;
  }
`;

const ComingSoonIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1.5rem;
  animation: float 3s ease-in-out infinite;

  @keyframes float {
    0%,
    100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-10px);
    }
  }

  @media (max-width: 768px) {
    font-size: 3.5rem;
    margin-bottom: 1.2rem;
  }

  @media (max-width: 480px) {
    font-size: 3rem;
    margin-bottom: 1rem;
  }
`;

const ComingSoonTitle = styled.h2`
  font-family: "Playfair Display", serif;
  font-size: 2rem;
  color: #e97d7d;
  margin-bottom: 1rem;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 0.8rem;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
    margin-bottom: 0.6rem;
  }
`;

const ComingSoonMessage = styled.p`
  font-size: 1.1rem;
  color: #666;
  line-height: 1.6;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
  }
`;

const ComingSoonSubtext = styled.p`
  font-size: 0.95rem;
  color: #999;
  margin-top: 1rem;
  font-style: italic;

  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

interface ShopProductGridProps {
  selectedCategory?: string | null;
  selectedProductId?: string | null;
}

const ShopProductGrid: React.FC<ShopProductGridProps> = ({
  selectedCategory,
  selectedProductId,
}) => {
  const [selected, setSelected] = useState<Product | null>(null);
  const { addToCart } = useCartStore();
  const { data: products = [], isLoading, error } = useProducts();
  // Track touch positions to distinguish scrolls from taps
  const touchStartPositions = React.useRef<{
    [key: string]: { x: number; y: number };
  }>({});
  const isScrolling = React.useRef<{ [key: string]: boolean }>({});

  // Reset selected product when category changes
  useEffect(() => {
    setSelected(null);
  }, [selectedCategory]);

  // Handle selectedProductId from featured products navigation
  useEffect(() => {
    if (selectedProductId && products.length > 0) {
      const product = products.find(
        (p: ApiProduct) => p.id === selectedProductId
      );
      if (product) {
        const productView = convertToProductView(product);
        setSelected(productView);
      }
    }
  }, [selectedProductId, products]);

  // Filter products based on selected category
  const filteredProducts = selectedCategory
    ? products.filter(
        (product: ApiProduct) => product.category === selectedCategory
      )
    : products;

  const convertToProductView = (apiProduct: ApiProduct): Product => {
    return {
      id: apiProduct.id,
      name: apiProduct.name,
      images: apiProduct.images,
      price: apiProduct.price,
      shortDescription: apiProduct.description.substring(0, 100) + "...",
      longDescription: apiProduct.description,
      inStock: apiProduct.stock > 0,
      rating: 4.5, // Default rating since not in API
      reviewsCount: 0, // Default reviews count
      materials: (apiProduct as any).materials,
      size: (apiProduct as any).size,
      idealFor: (apiProduct as any).idealFor,
    };
  };

  const handleAddToCart = (apiProduct: ApiProduct) => {
    addToCart({
      id: apiProduct.id,
      name: apiProduct.name,
      price: apiProduct.price,
      qty: 1,
      images: apiProduct.images,
    });
  };

  if (isLoading) {
    return (
      <Container>
        <Grid>
          {Array.from({ length: 6 }).map((_, index) => (
            <SkeletonCard key={index}>
              <SkeletonImage />
              <SkeletonText />
              <SkeletonPrice />
              <SkeletonButton />
            </SkeletonCard>
          ))}
        </Grid>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <div
          style={{
            textAlign: "center",
            padding: "2rem",
            fontSize: "1.1rem",
            color: "#e97d7d",
          }}
        >
          Failed to load products. Please try again later.
        </div>
      </Container>
    );
  }

  return (
    <Container>
      {selected ? (
        <>
          <ProductView product={selected} />
          <Grid>
            {filteredProducts.map((apiProduct: ApiProduct) => {
              const prod = convertToProductView(apiProduct);
              return (
                <Card
                  key={prod.id}
                  onClick={() => {
                    setSelected(prod);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                >
                  <ProductImage
                    src={prod.images[0] || "/category1.PNG"}
                    alt={prod.name}
                  />
                  {prod.images.length > 1 && (
                    <ImageCount>{prod.images.length} photos</ImageCount>
                  )}
                  <ProductTitle>{prod.name}</ProductTitle>
                  <ProductPrice>${prod.price.toFixed(2)}</ProductPrice>
                  <AddToCart
                    title="Add to Cart"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToCart(apiProduct);
                    }}
                  >
                    +
                  </AddToCart>
                </Card>
              );
            })}
          </Grid>
        </>
      ) : (
        <>
          {filteredProducts.length === 0 ? (
            <ComingSoonContainer>
              <ComingSoonIcon>✨</ComingSoonIcon>
              <ComingSoonTitle>
                {selectedCategory
                  ? `${selectedCategory} Coming Soon!`
                  : "Coming Soon!"}
              </ComingSoonTitle>
              <ComingSoonMessage>
                {selectedCategory
                  ? `We're crafting amazing ${selectedCategory.toLowerCase()} pieces just for you.`
                  : "We're working on bringing you amazing handcrafted products."}
              </ComingSoonMessage>
              <ComingSoonMessage>
                Check back soon for new arrivals!
              </ComingSoonMessage>
              <ComingSoonSubtext>
                Handcrafted with ❤️ in Toronto, Canada
              </ComingSoonSubtext>
            </ComingSoonContainer>
          ) : (
            <Grid>
              {filteredProducts.map((apiProduct: ApiProduct) => {
                const prod = convertToProductView(apiProduct);
                return (
                  <Card
                    key={prod.id}
                    onClick={(e) => {
                      // Check if click was on AddToCart button (desktop only)
                      const target = e.target as HTMLElement;
                      if (!target.closest('button[title="Add to Cart"]')) {
                        setSelected(prod);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }
                    }}
                    onTouchStart={(e) => {
                      // Record initial touch position
                      const touch = e.touches[0];
                      touchStartPositions.current[prod.id] = {
                        x: touch.clientX,
                        y: touch.clientY,
                      };
                      isScrolling.current[prod.id] = false;
                    }}
                    onTouchMove={(e) => {
                      // Detect if user is scrolling
                      const touch = e.touches[0];
                      const startPos = touchStartPositions.current[prod.id];
                      if (startPos) {
                        const deltaX = Math.abs(touch.clientX - startPos.x);
                        const deltaY = Math.abs(touch.clientY - startPos.y);
                        // If movement is more than 10px, consider it a scroll
                        if (deltaX > 10 || deltaY > 10) {
                          isScrolling.current[prod.id] = true;
                        }
                      }
                    }}
                    onTouchEnd={(e) => {
                      // Only trigger click if it wasn't a scroll
                      if (!isScrolling.current[prod.id]) {
                        setSelected(prod);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }
                      // Clean up
                      delete touchStartPositions.current[prod.id];
                      delete isScrolling.current[prod.id];
                    }}
                  >
                    <ProductImage
                      src={prod.images[0] || "/category1.PNG"}
                      alt={prod.name}
                    />
                    {prod.images.length > 1 && (
                      <ImageCount>{prod.images.length} photos</ImageCount>
                    )}
                    <ProductTitle>{prod.name}</ProductTitle>
                    <ProductPrice>${prod.price.toFixed(2)}</ProductPrice>
                    <AddToCart
                      title="Add to Cart"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleAddToCart(apiProduct);
                      }}
                      onTouchEnd={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleAddToCart(apiProduct);
                      }}
                    >
                      +
                    </AddToCart>
                  </Card>
                );
              })}
            </Grid>
          )}
        </>
      )}
    </Container>
  );
};

export default ShopProductGrid;
